/**
 * Heat Map Component
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, Property, NotifyPropertyChanges, Internationalization, Complex, isNullOrUndefined, SanitizeHtmlHelper } from '@syncfusion/ej2-base';
import { remove, Event, EventHandler, Touch } from '@syncfusion/ej2-base';
// eslint-disable-next-line
import { Browser } from '@syncfusion/ej2-base';
import { SvgRenderer, CanvasRenderer } from '@syncfusion/ej2-svg-base';
import { Size, stringToNumber, RectOption, Rect, TextBasic, measureText, removeMeasureElement } from './utils/helper';
import { DrawSvgCanvas, TextOption, titlePositionX, getTitle, showTooltip, getElement, SelectedCellDetails } from './utils/helper';
import { removeElement, CanvasTooltip, getTooltipText } from './utils/helper';
import { Margin, Title } from './model/base';
import { Theme, getThemeColor } from './model/theme';
import { Axis } from './axis/axis';
import { AxisHelper } from './axis/axis-helpers';
import { Series, CellSettings } from './series/series';
import { PaletteSettings, CellColor } from './utils/colorMapping';
import { TooltipSettings } from './utils/tooltip';
import { TwoDimensional } from './datasource/twodimensional';
import { LegendSettings } from '../heatmap/legend/legend';
import { Data } from './datasource/adaptor';
import { ExportUtils } from '../heatmap/utils/export';
/**
 * Represents the heatmap control. This is used to customize the properties of the heatmap in order to visualize two-dimensional data, with values represented by gradient or solid color variations.
 * ```html
 * <div id="container"/>
 * <script>
 *   var heatmapObj = new HeatMap();
 *   heatmapObj.appendTo("#container");
 * </script>
 * ```
 */
let HeatMap = class HeatMap extends Component {
    /**
     * Represents the heatmap control. This is used to customize the properties of the heatmap in order to visualize two-dimensional data, with values represented by gradient or solid color variations.
     * ```html
     * <div id="container"/>
     * <script>
     *   var heatmapObj = new HeatMap();
     *   heatmapObj.appendTo("#container");
     * </script>
     * ```
     */
    constructor() {
        /**
         * Sets and gets the width of the heatmap. The width of the heatmap accepts pixel or percentage values given in string format.
         *
         * If specified as '100%, heatmap renders to the full width of its parent element.
         *
         * @default null
         */
        super(...arguments);
        /** @private */
        this.enableCanvasRendering = false;
        /** @private */
        this.isColorRange = false;
        /** @private */
        this.isCellTapHold = false;
        /** @private */
        this.selectedCellCount = 0;
        /** @private */
        this.toggleValue = [];
        /** @private */
        this.legendOnLoad = true;
        /** @private */
        this.resizing = false;
        /** @private */
        this.rendering = true;
        /** @private */
        this.multiSelection = false;
        /** @private */
        this.rectSelected = false;
        /** @private */
        this.previousSelectedCellsRect = [];
        /** @private */
        this.multiCellCollection = [];
        /** @private */
        this.selectedMultiCellCollection = [];
        /** @private */
        this.tempMultiCellCollection = [];
        /**
         * @private
         */
        this.tooltipCollection = [];
        /**
         * @private
         */
        this.isCellData = false;
    }
    preRender() {
        this.initPrivateVariable();
        this.unWireEvents();
        this.wireEvents();
    }
    /**
     * This method is used to perform the export functionality for the heatmap.
     *
     * @param {ExportType} type - Specifies the type of the exported file.
     * @param {string} fileName - Specifies the file name for the exported file.
     * @param {PdfPageOrientation} orientation - Specifies the orientation for the exported PDF document.
     */
    export(type, fileName, orientation) {
        const exportMap = new ExportUtils(this);
        exportMap.export(type, fileName, orientation);
    }
    initPrivateVariable() {
        this.renderer = new SvgRenderer(this.element.id);
        this.canvasRenderer = new CanvasRenderer(this.element.id);
        this.secondaryCanvasRenderer = new CanvasRenderer(this.element.id + '_secondary');
        this.heatMapAxis = new AxisHelper(this);
        this.heatMapSeries = new Series(this);
        this.drawSvgCanvas = new DrawSvgCanvas(this);
        this.twoDimensional = new TwoDimensional(this);
        this.cellColor = new CellColor(this);
        this.tempRectHoverClass = '';
        this.tempTooltipRectId = '';
        this.setCulture();
    }
    /**
     * Method to set culture for heatmap
     */
    setCulture() {
        this.intl = new Internationalization();
    }
    render() {
        this.horizontalGradient = this.legendSettings.position === 'Bottom' || this.legendSettings.position === 'Top';
        this.updateBubbleHelperProperty();
        this.trigger('load', { heatmap: this });
        if (this.theme === 'TailwindDark' || this.theme === 'Tailwind') {
            const textSettings = { title: { textStyle: { size: '12px', fontFamily: 'Inter', fontWeight: '500' } }, textStyle: { size: '12px', fontFamily: 'Inter' } };
            this.setProperties({ titleSettings: { textStyle: { size: '14px', fontFamily: 'Inter' } } }, true);
            this.setProperties({ legendSettings: textSettings }, true);
            this.setProperties({ xAxis: textSettings }, true);
            this.setProperties({ yAxis: textSettings }, true);
            this.setProperties({ cellSettings: { textStyle: { fontFamily: 'Inter' } } }, true);
        }
        if (this.theme === 'Tailwind3Dark' || this.theme === 'Tailwind3') {
            const textSettings = { title: { textStyle: { size: '12px', fontFamily: 'Inter', fontWeight: '500' } }, textStyle: { size: '12px', fontFamily: 'Inter', fontWeight: '500' } };
            this.setProperties({ titleSettings: { textStyle: { size: '14px', fontFamily: 'Inter', fontWeight: '600' } } }, true);
            this.setProperties({ legendSettings: textSettings }, true);
            this.setProperties({ xAxis: { title: { textStyle: { size: '12px', fontFamily: 'Inter', fontWeight: '500' } }, textStyle: { size: '12px', fontFamily: 'Inter', fontWeight: '400' } } }, true);
            this.setProperties({ yAxis: { title: { textStyle: { size: '12px', fontFamily: 'Inter', fontWeight: '500' } }, textStyle: { size: '12px', fontFamily: 'Inter', fontWeight: '400' } } }, true);
            this.setProperties({ cellSettings: { textStyle: { fontFamily: 'Inter', fontWeight: '400' } } }, true);
        }
        if (this.theme === 'Material3' || this.theme === 'Material3Dark') {
            const textSettings = { title: { textStyle: { size: '14px', fontFamily: 'Roboto', fontWeight: '500' } }, textStyle: { size: '12px', fontFamily: 'Roboto', fontWeight: '400' } };
            this.setProperties({ titleSettings: { textStyle: { size: '16px', fontFamily: 'Roboto' } } }, true);
            this.setProperties({ legendSettings: textSettings }, true);
            this.setProperties({ xAxis: textSettings }, true);
            this.setProperties({ yAxis: textSettings }, true);
            this.setProperties({ cellSettings: { textStyle: { fontFamily: 'Roboto', fontWeight: '400' } } }, true);
        }
        if (this.theme === 'Bootstrap5' || this.theme === 'Bootstrap5Dark') {
            const textSettings = { title: { textStyle: { size: '12px', fontFamily: 'Segoe UI', fontWeight: '400' } }, textStyle: { size: '12px', fontFamily: 'Segoe UI', fontWeight: '400' } };
            this.setProperties({ titleSettings: { textStyle: { size: '14px', fontFamily: 'Segoe UI', fontWeight: '400' } } }, true);
            this.setProperties({ legendSettings: textSettings }, true);
            this.setProperties({ xAxis: textSettings }, true);
            this.setProperties({ yAxis: textSettings }, true);
            this.setProperties({ cellSettings: { textStyle: { size: '10px', fontWeight: '400', fontFamily: 'Segoe UI' } } }, true);
        }
        if (this.theme === 'Fluent' || this.theme === 'FluentDark') {
            const textSettings = { title: { textStyle: { size: '12px', fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", sans-serif', fontWeight: '500' } }, textStyle: { size: '12px', fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"' } };
            this.setProperties({ titleSettings: { textStyle: { size: '16px', fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", sans-serif' } } }, true);
            this.setProperties({ legendSettings: textSettings }, true);
            this.setProperties({ xAxis: textSettings }, true);
            this.setProperties({ yAxis: textSettings }, true);
            this.setProperties({ cellSettings: { textStyle: { fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", sans-serif' } } }, true);
        }
        if (this.theme === 'Fluent2' || this.theme === 'Fluent2Dark' || this.theme === 'Fluent2HighContrast') {
            const textSettings = { title: { textStyle: { size: '12px', fontFamily: 'Segoe UI', fontWeight: '400' } }, textStyle: { size: '12px', fontFamily: 'Segoe UI', fontWeight: '400' } };
            this.setProperties({ titleSettings: { textStyle: { size: '14px', fontFamily: 'Segoe UI', fontWeight: '600' } } }, true);
            this.setProperties({ legendSettings: textSettings }, true);
            this.setProperties({ xAxis: textSettings }, true);
            this.setProperties({ yAxis: textSettings }, true);
            this.setProperties({ cellSettings: { textStyle: { size: '10px', fontWeight: '400', fontFamily: 'Segoe UI' } } }, true);
        }
        this.initAxis();
        this.processInitData();
        this.setTheme();
        this.calculateMaxLength();
        this.heatMapAxis.calculateVisibleLabels();
        this.twoDimensional.processDataSource(this.completeAdaptDataSource);
        this.createSvg();
        this.cellColor.getColorCollection();
        this.calculateBounds();
        this.renderElements();
        this.appendSvgObject();
        if (this.tooltipModule) {
            this.tooltipModule.showHideTooltip(false);
        }
        this.renderComplete();
    }
    /**
     * To re-calculate the datasource while changing datasource property dynamically.
     *
     * @private
     */
    reRenderDatasource() {
        this.dataSourceMinValue = null;
        this.dataSourceMaxValue = null;
        this.processInitData();
        this.calculateMaxLength();
        this.heatMapAxis.calculateVisibleLabels();
        this.twoDimensional.processDataSource(this.completeAdaptDataSource);
        this.cellColor.getColorCollection();
        this.calculateBounds();
    }
    /**
     * To process datasource property.
     *
     * @private
     */
    processInitData() {
        if (this.adaptorModule) {
            this.adaptorModule.constructDatasource(this.dataSource, this.dataSourceSettings);
        }
        else {
            this.completeAdaptDataSource = this.dataSource;
        }
    }
    /**
     * To set render mode of heatmap as SVG or Canvas.
     *
     * @private
     */
    setRenderMode() {
        if (this.renderingMode === 'Canvas') {
            this.enableCanvasRendering = true;
        }
        else if (this.renderingMode === 'Auto' &&
            (this.axisCollections[0].axisLabelSize * this.axisCollections[1].axisLabelSize) >= 10000) {
            this.enableCanvasRendering = true;
        }
        else {
            this.enableCanvasRendering = false;
        }
    }
    /**
     * To set bubble helper private property.
     *
     * @private
     */
    updateBubbleHelperProperty() {
        if (isNullOrUndefined(this.legendModule) || (this.cellSettings.tileType === 'Bubble' &&
            (this.cellSettings.bubbleType === 'Size' || this.cellSettings.bubbleType === 'Sector'))) {
            this.legendVisibilityByCellType = false;
        }
        else if (this.legendModule && this.legendSettings.visible) {
            this.legendVisibilityByCellType = true;
        }
        if (this.cellSettings.tileType === 'Bubble' && this.cellSettings.bubbleType === 'SizeAndColor') {
            this.bubbleSizeWithColor = true;
        }
        else {
            this.bubbleSizeWithColor = false;
        }
    }
    renderElements() {
        this.tooltipCollection = [];
        this.renderSecondaryElement();
        this.renderBorder();
        this.renderTitle();
        this.heatMapAxis.renderAxes();
        if (this.tooltipModule && this.showTooltip) {
            this.tooltipModule.tooltipObject = null;
            this.tooltipModule.createTooltipDiv(this);
        }
        this.heatMapSeries.renderRectSeries();
        if (this.legendModule && this.legendSettings.visible
            && this.legendVisibilityByCellType) {
            this.legendModule.renderLegendItems();
            if (this.paletteSettings.type === 'Fixed' && this.legendSettings.enableSmartLegend &&
                this.legendSettings.labelDisplayType === 'None') {
                this.legendModule.createTooltipDiv();
            }
        }
        removeMeasureElement();
    }
    /**
     * Get component name
     *
     * @private
     */
    getModuleName() {
        return 'heatmap';
    }
    /**
     * Get the properties to be maintained in the persisted state.
     *
     * @private
     */
    getPersistData() {
        return '';
    }
    /**
     * @private
     */
    // tslint:disable-next-line:max-func-body-length
    onPropertyChanged(newProp, oldProp) {
        let renderer = false;
        let refreshBounds = false;
        let isUpdateSelection = true;
        for (const prop of Object.keys(newProp)) {
            switch (prop) {
                case 'renderingMode':
                    this.rendering = false;
                    isUpdateSelection = false;
                    renderer = true;
                    break;
                case 'cellSettings':
                    this.updateBubbleHelperProperty();
                    if (this.legendModule && ((newProp.cellSettings.tileType !== (oldProp.cellSettings
                        !== undefined && oldProp.cellSettings.tileType))
                        || (newProp.cellSettings.bubbleType !== oldProp.cellSettings.bubbleType))) {
                        this.legendOnLoad = true;
                        this.legendModule.updateLegendRangeCollections();
                    }
                    if (this.cellSettings.tileType === 'Bubble') {
                        isUpdateSelection = false;
                    }
                    this.reRenderDatasource();
                    refreshBounds = true;
                    break;
                case 'showTooltip':
                    refreshBounds = true;
                    break;
                case 'dataSource':
                case 'dataSourceSettings':
                    this.isCellData = false;
                    this.paletteCellSelectionUpdation();
                    this.reRenderDatasource();
                    isUpdateSelection = false;
                    renderer = true;
                    break;
                case 'titleSettings':
                case 'width':
                case 'height':
                case 'margin':
                case 'backgroundColor':
                    refreshBounds = true;
                    break;
                case 'legendSettings':
                    this.updateBubbleHelperProperty();
                    if (this.legendModule && this.legendVisibilityByCellType && (((newProp.legendSettings.visible !==
                        (oldProp.legendSettings !== undefined && oldProp.legendSettings.visible)) ||
                        (newProp.legendSettings.enableSmartLegend !== oldProp.legendSettings.enableSmartLegend)))) {
                        this.legendOnLoad = true;
                        this.legendModule.updateLegendRangeCollections();
                    }
                    else {
                        this.legendOnLoad = false;
                    }
                    refreshBounds = true;
                    break;
                case 'yAxis':
                case 'xAxis':
                    this.paletteCellSelectionUpdation();
                    this.reRenderDatasource();
                    isUpdateSelection = false;
                    refreshBounds = true;
                    break;
                case 'paletteSettings':
                    this.paletteCellSelectionUpdation();
                    this.twoDimensional.processDataSource(this.completeAdaptDataSource);
                    this.cellColor.getColorCollection();
                    this.calculateBounds();
                    renderer = true;
                    break;
                case 'theme':
                    this.setTheme();
                    renderer = true;
                    break;
                case 'tooltipSettings':
                    if (this.tooltipModule) {
                        this.tooltipModule.tooltipObject.fill = this.tooltipSettings.fill;
                        this.tooltipModule.tooltipObject.border = this.tooltipSettings.border;
                        this.tooltipModule.tooltipObject.textStyle = this.tooltipSettings.textStyle;
                        this.tooltipModule.tooltipObject.template = this.tooltipSettings.template;
                        this.tooltipModule.tooltipObject.refresh();
                    }
                    break;
            }
        }
        if (!refreshBounds && renderer) {
            this.createSvg();
            this.renderElements();
            this.appendSvgObject();
            this.trigger('created');
            if (!isUpdateSelection) {
                this.clearSelection();
            }
        }
        else if (refreshBounds) {
            this.createSvg();
            this.refreshBound();
            this.appendSvgObject();
            this.trigger('created');
        }
        if (this.allowSelection && this.rectSelected) {
            if (isUpdateSelection) {
                this.updateCellSelection();
            }
            else {
                this.clearSelection();
            }
        }
        this.rendering = true;
    }
    paletteCellSelectionUpdation() {
        this.updateBubbleHelperProperty();
        if (this.legendModule && this.legendVisibilityByCellType) {
            this.legendOnLoad = true;
            this.legendModule.updateLegendRangeCollections();
        }
    }
    /**
     * create svg or canvas element
     *
     * @private
     */
    createSvg() {
        this.removeSvg();
        this.setRenderMode();
        this.calculateSize();
        if (!this.enableCanvasRendering) {
            this.svgObject = this.renderer.createSvg({
                id: this.element.id + '_svg',
                width: this.availableSize.width,
                height: this.availableSize.height
            });
            if (this.cellSettings.border.width.toString() === '0' && this.cellSettings.tileType === 'Rect') {
                this.svgObject.setAttribute('shape-rendering', 'crispEdges');
            }
        }
        else {
            this.svgObject = this.canvasRenderer.createCanvas({
                id: this.element.id + '_canvas',
                width: this.availableSize.width,
                height: this.availableSize.height
            });
            if (this.allowSelection) {
                this.createMultiCellDiv(true);
            }
        }
    }
    /**
     *  To Remove the SVG.
     *
     * @private
     */
    removeSvg() {
        if (document.getElementById(this.element.id + '_Secondary_Element')) {
            remove(document.getElementById(this.element.id + '_Secondary_Element'));
        }
        if (document.getElementById(this.element.id + 'Celltooltipcontainer')) {
            remove(document.getElementById(this.element.id + 'Celltooltipcontainer'));
        }
        if (document.getElementById(this.element.id + 'legendLabelTooltipContainer')) {
            remove(document.getElementById(this.element.id + 'legendLabelTooltipContainer'));
        }
        if (document.getElementById(this.element.id + '_Multi_CellSelection_Canvas')) {
            remove(document.getElementById(this.element.id + '_Multi_CellSelection_Canvas'));
        }
        if (document.getElementById(this.element.id + '_CellSelection_Container')) {
            remove(document.getElementById(this.element.id + '_CellSelection_Container'));
        }
        if (document.getElementById(this.element.id + '_secondary_canvas')) {
            remove(document.getElementById(this.element.id + '_secondary_canvas'));
        }
        if (this.svgObject) {
            const svgElement = document.getElementById(this.svgObject.id);
            if (svgElement) {
                while (this.svgObject.childNodes.length) {
                    this.svgObject.removeChild(this.svgObject.firstChild);
                }
                remove(this.svgObject);
            }
        }
    }
    renderSecondaryElement() {
        const tooltipDiv = this.createElement('div');
        tooltipDiv.id = this.element.id + '_Secondary_Element';
        this.element.appendChild(tooltipDiv);
        const divElement = this.createElement('div', {
            id: this.element.id + '_CellSelection_Container'
        });
        divElement.style.cssText = 'position:absolute; z-index: 2 ; top:' + this.initialClipRect.y + 'px' + '; left:' + this.initialClipRect.x + 'px';
        this.element.appendChild(divElement);
    }
    /**
     * To provide the array of modules needed for control rendering
     *
     * @returns {ModuleDeclaration[]}
     * @private
     */
    requiredModules() {
        const modules = [];
        if (this.showTooltip) {
            modules.push({
                member: 'Tooltip',
                args: [this],
                name: 'Tooltip'
            });
        }
        if (this.legendSettings.visible) {
            modules.push({
                member: 'Legend',
                args: [this],
                name: 'Legend'
            });
        }
        if (!isNullOrUndefined(this.dataSourceSettings) && ((this.dataSourceSettings.adaptorType === 'Table' && this.dataSourceSettings.isJsonData) || this.dataSourceSettings.adaptorType === 'Cell')) {
            modules.push({
                member: 'Adaptor',
                args: [this],
                name: 'Adaptor'
            });
        }
        return modules;
    }
    /**
     * This method destroys the heatmap. This method removes the events associated with the heatmap and disposes the objects created for rendering and updating the heatmap.
     * {% codeBlock src='heatmap/destroy/index.md' %}{% endcodeBlock %}
     *
     * @function destroy
     * @returns {void}.
     * @member of Heatmap
     */
    destroy() {
        this.unWireEvents();
        this.touchInstance.destroy();
        this.touchInstance = null;
        for (let i = 0; i < this.axisCollections.length; i++) {
            this.axisCollections[i].destroy();
        }
        this.axisCollections = null;
        if (!isNullOrUndefined(this.heatMapSeries)) {
            this.heatMapSeries.destroy();
        }
        this.heatMapSeries = null;
        if (!isNullOrUndefined(this.heatMapAxis)) {
            this.heatMapAxis.destroy();
        }
        this.heatMapAxis = null;
        super.destroy();
        removeMeasureElement();
        if (!isNullOrUndefined(this.twoDimensional)) {
            this.twoDimensional.destroy();
        }
        this.twoDimensional = null;
        this.element.innerHTML = '';
        this.availableSize = null;
        this.elementSize = null;
        this.initialClipRect = null;
        this.element.classList.remove('e-heatmap');
        this.drawSvgCanvas = null;
        this.cellColor = null;
        this.colorCollection = null;
        this.legendColorCollection = null;
        this.clonedDataSource = null;
        this.completeAdaptDataSource = null;
        this.currentRect = null;
        this.dataMax = null;
        this.dataMin = null;
        this.previousRect = null;
        this.selectedCellsRect = null;
        this.canvasSelectedCells = null;
        this.titleRect = null;
        this.tooltipCollection = null;
        this.border = null;
        this.intl = null;
        this.titleCollection = null;
        this.themeStyle = null;
        this.renderer = null;
        this.canvasRenderer = null;
        this.secondaryCanvasRenderer = null;
        this.svgObject = null;
        this.resizeEvent = null;
        this.toggleValue = [];
        this.previousSelectedCellsRect = [];
        this.selectedMultiCellCollection = [];
        this.tempMultiCellCollection = [];
    }
    /**
     * Applies all the pending property changes and render the component again.
     *
     * @function destroy
     * @returns {void}.
     */
    refresh() {
        super.refresh();
        this.element.classList.add('e-heatmap');
    }
    /**
     * Appending svg object to the element
     *
     * @private
     */
    appendSvgObject() {
        this.element.appendChild(this.svgObject);
        if (this.enableCanvasRendering && this.allowSelection) {
            this.createMultiCellDiv(false);
        }
    }
    renderBorder() {
        this.border = {
            width: 0
        };
        const background = !isNullOrUndefined(this.backgroundColor) ? this.backgroundColor : this.themeStyle.background;
        const width = 0;
        const rect = new RectOption(this.element.id + '_HeatmapBorder', background, this.border, 1, new Rect(width / 2, width / 2, this.availableSize.width - width, this.availableSize.height - width));
        this.drawSvgCanvas.drawRectangle(rect, this.svgObject);
    }
    calculateSize() {
        const width = stringToNumber(this.width, this.element.offsetWidth) || this.element.offsetWidth || 600;
        const height = stringToNumber(this.height, this.element.offsetHeight) || this.element.offsetHeight || 450;
        this.availableSize = new Size(width, height);
        let alignElement = this.element;
        while (alignElement.parentNode) {
            if (alignElement.tagName === 'BODY') {
                break;
            }
            const align = alignElement.align;
            if (align === 'center') {
                const containerWidth = this.availableSize.width.toString();
                this.element.style.width = containerWidth + 'px';
                this.element.style.margin = '0 auto';
                break;
            }
            alignElement = alignElement.parentElement;
        }
    }
    renderTitle() {
        if (this.titleSettings.text) {
            const titleStyle = this.titleSettings.textStyle;
            const anchor = titleStyle.textAlignment === 'Near' ? 'start' :
                titleStyle.textAlignment === 'Far' ? 'end' : 'middle';
            this.elementSize = measureText(this.titleCollection[0], titleStyle);
            const options = new TextOption(this.element.id + '_HeatmapTitle', new TextBasic(titlePositionX(this.availableSize.width - this.margin.left - this.margin.right, this.margin.left, this.margin.right, titleStyle), this.margin.top + ((this.elementSize.height) * 3 / 4), anchor, this.titleCollection), titleStyle, titleStyle.color || this.themeStyle.heatMapTitle);
            if (this.titleCollection.length > 1) {
                this.drawSvgCanvas.createWrapText(options, titleStyle, this.svgObject);
            }
            else {
                this.drawSvgCanvas.createText(options, this.svgObject, this.titleCollection[0]);
                if (this.titleCollection[0].indexOf('...') !== -1 && this.enableCanvasRendering) {
                    this.tooltipCollection.push(new CanvasTooltip(this.titleSettings.text, new Rect(this.margin.left, this.margin.top, this.elementSize.width, this.elementSize.height)));
                }
            }
        }
    }
    titleTooltip(event, x, y, isTouch) {
        const targetId = event.target.id;
        if ((targetId === (this.element.id + '_HeatmapTitle')) && (event.target.textContent.indexOf('...') > -1)) {
            showTooltip(this.titleSettings.text, x, y, this.element.offsetWidth, this.element.id + '_Title_Tooltip', getElement(this.element.id + '_Secondary_Element'), isTouch, this);
        }
        else {
            removeElement(this.element.id + '_Title_Tooltip');
        }
    }
    // eslint-disable-next-line
    axisTooltip(event, x, y, isTouch) {
        const targetId = event.target.id;
        if ((targetId.indexOf(this.element.id + '_XAxis_Label') !== -1) ||
            (targetId.indexOf(this.element.id + '_YAxis_Label') !== -1) ||
            (targetId.indexOf(this.element.id + '_XAxis_MultiLevel') !== -1) ||
            (targetId.indexOf(this.element.id + '_YAxis_MultiLevel') !== -1)) {
            const tooltipText = getTooltipText(this.tooltipCollection, x, y);
            if (tooltipText) {
                showTooltip(tooltipText, x, y, this.element.offsetWidth, this.element.id + '_axis_Tooltip', getElement(this.element.id + '_Secondary_Element'), this.isTouch, this);
            }
            else {
                removeElement(this.element.id + '_axis_Tooltip');
            }
        }
        else {
            removeElement(this.element.id + '_axis_Tooltip');
        }
    }
    isHeatmapRect(x, y) {
        const firstRectDetails = [];
        const lastRectDetails = [];
        let isRect;
        const borderBoundary = 5;
        if (this.heatMapSeries.rectPositionCollection.length > 0) {
            firstRectDetails.push(this.heatMapSeries.rectPositionCollection[0][0]);
            lastRectDetails.push(this.heatMapSeries.rectPositionCollection[this.yLength - 1][this.xLength - 1]);
        }
        if (firstRectDetails.length > 0 && lastRectDetails.length > 0) {
            if (this.cellSettings.border.width > borderBoundary && (x >= firstRectDetails[0].x && y >= firstRectDetails[0].y &&
                x <= (lastRectDetails[0].x + lastRectDetails[0].width) &&
                y <= (lastRectDetails[0].y + lastRectDetails[0].height)) && this.cellSettings.tileType === 'Rect') {
                const currentRect = this.heatMapSeries.getCurrentRect(x, y);
                const rectHeight = lastRectDetails[0].height;
                const rectWidth = lastRectDetails[0].width;
                const cellBorder = this.cellSettings.border.width / 2;
                if ((x >= (currentRect.x + cellBorder) && (y >= (currentRect.y + cellBorder)) &&
                    (x <= (currentRect.x + (rectWidth - cellBorder)) &&
                        (y <= (currentRect.y + (rectHeight - cellBorder)))))) {
                    isRect = true;
                    this.isRectBoundary = true;
                }
                else {
                    isRect = false;
                    this.isRectBoundary = false;
                }
            }
            else {
                isRect = (x >= firstRectDetails[0].x && y >= firstRectDetails[0].y &&
                    x <= (lastRectDetails[0].x + lastRectDetails[0].width) &&
                    y <= (lastRectDetails[0].y + lastRectDetails[0].height));
                this.isRectBoundary = isRect;
            }
        }
        return isRect;
    }
    setTheme() {
        /*! Set theme */
        this.themeStyle = getThemeColor(this.theme);
    }
    calculateBounds() {
        const margin = this.margin;
        // Title Height;
        let titleHeight = 0;
        const padding = (this.legendModule && this.legendSettings.position === 'Top'
            && this.legendVisibilityByCellType) || this.titleSettings.textStyle.size === '0px' ? 0 : 16; // title padding
        const left = margin.left;
        const width = this.availableSize.width - left - margin.right;
        if ((this.paletteSettings.colorGradientMode === 'Column' || this.paletteSettings.colorGradientMode === 'Row') &&
            this.paletteSettings.type === 'Gradient') {
            if (this.paletteSettings.palette.length === 0) {
                this.legendVisibilityByCellType = false;
            }
            else {
                for (let i = 0; i < this.paletteSettings.palette.length; i++) {
                    if (this.paletteSettings.palette[i].value !== null || '') {
                        this.legendVisibilityByCellType = true;
                    }
                    else if (this.paletteSettings.palette[i].value === null || '') {
                        this.legendVisibilityByCellType = false;
                        break;
                    }
                }
            }
        }
        if (this.titleSettings.text) {
            const titleText = this.enableHtmlSanitizer ? SanitizeHtmlHelper.sanitize(this.titleSettings.text) :
                this.titleSettings.text;
            this.titleCollection = getTitle(titleText, this.titleSettings.textStyle, width);
            titleHeight = (measureText(titleText, this.titleSettings.textStyle).height * this.titleCollection.length) +
                padding;
        }
        const top = margin.top + titleHeight;
        this.titleRect = new Rect(margin.left, margin.top, this.availableSize.width - margin.left - margin.right, titleHeight);
        const height = this.availableSize.height - top - margin.bottom;
        this.initialClipRect = new Rect(left, top, width, height);
        const legendTop = this.initialClipRect.y;
        if (this.legendModule && this.legendSettings.visible && this.legendVisibilityByCellType) {
            this.legendModule.calculateLegendBounds(this.initialClipRect);
        }
        this.heatMapAxis.measureAxis(this.initialClipRect);
        if (this.legendModule && this.legendSettings.visible && this.legendVisibilityByCellType) {
            this.legendModule.calculateLegendSize(this.initialClipRect, legendTop);
        }
        this.heatMapAxis.calculateAxisSize(this.initialClipRect);
    }
    refreshBound() {
        this.updateBubbleHelperProperty();
        this.calculateBounds();
        this.renderElements();
    }
    initAxis() {
        let axis;
        const axes = [this.xAxis, this.yAxis];
        this.axisCollections = [];
        for (let i = 0, len = axes.length; i < len; i++) {
            axis = axes[i];
            axis.orientation = (i === 0) ? 'Horizontal' : 'Vertical';
            axis.jsonCellLabel = [];
            this.axisCollections.push(axis);
        }
    }
    /**
     * Method to bind events for HeatMap
     */
    wireEvents() {
        /*! Find the Events type */
        // eslint-disable-next-line
        const isIE11Pointer = Browser.isPointer;
        const start = Browser.touchStartEvent;
        const stop = Browser.touchEndEvent;
        const move = Browser.touchMoveEvent;
        const cancel = isIE11Pointer ? 'pointerleave' : 'mouseleave';
        EventHandler.add(this.element, Browser.isDevice ? start : 'click', this.heatMapMouseClick, this);
        EventHandler.add(this.element, 'contextmenu', this.heatMapMouseRightClick, this);
        EventHandler.add(this.element, 'dblclick', this.heatMapMouseDoubleClick, this);
        EventHandler.add(this.element, start, this.heatMapMouseMove, this);
        EventHandler.add(this.element, stop, this.heatMapMouseLeave, this);
        EventHandler.add(this.element, move, this.heatMapMouseMove, this);
        EventHandler.add(this.element, cancel, this.heatMapMouseLeave, this);
        EventHandler.add(this.element, 'keyup', this.heatMapKeyUp, this);
        EventHandler.add(this.element, 'keydown', this.heatMapKeyDown, this);
        this.resizeEvent = this.heatMapResize.bind(this);
        window.addEventListener((Browser.isTouch && ('orientation' in window && 'onorientationchange' in window)) ? 'orientationchange' : 'resize', this.resizeEvent);
        // eslint-disable-next-line
        const heatmap = this;
        /**
         * Support for touch tapHold and tap for HeatMap
         */
        this.touchInstance = new Touch(this.element, {
            tapHold: (e) => {
                const targetId = e.originalEvent.target.id;
                if ((targetId.indexOf(this.element.id + '_HeatMapRect_') !== -1 || targetId.indexOf(this.element.id + '_HeatMapRectLabels_') !== -1) && this.allowSelection) {
                    heatmap.isCellTapHold = true;
                    const selectedCellCollection = [];
                    for (let i = 0; i < this.multiCellCollection.length; i++) {
                        selectedCellCollection.push(this.multiCellCollection[i]);
                    }
                    if (!e.originalEvent.ctrlKey || !this.enableMultiSelect) {
                        this.multiCellCollection = [];
                    }
                    heatmap.getDataCollection();
                    const argData = {
                        heatmap: heatmap,
                        cancel: false,
                        name: 'cellSelected',
                        data: heatmap.multiCellCollection
                    };
                    heatmap.trigger('cellSelected', argData);
                    if (!argData.cancel) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        if (e.ctrlKey === false || !this.enableMultiSelect) {
                            this.removeSelectedCellsBorder(false);
                        }
                        heatmap.currentRect.allowCollection = false;
                    }
                    else {
                        this.multiCellCollection = selectedCellCollection;
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        if (this.multiCellCollection.length > 0 || e.ctrlKey === false || !this.enableMultiSelect) {
                            this.removeSelectedCellsBorder(true);
                        }
                    }
                    heatmap.setCellOpacity();
                    window.clearTimeout(this.tooltipTimer);
                    heatmap.tooltipOnMouseMove(null, heatmap.currentRect, heatmap.isCellTapHold);
                }
            },
            // eslint-disable-next-line @typescript-eslint/tslint/config
            tap: (e) => {
                const targetId = e.originalEvent.target.id;
                if ((targetId.indexOf(this.element.id + '_HeatMapRect_') !== -1 || targetId.indexOf(this.element.id + '_HeatMapRectLabels_') !== -1 || targetId.indexOf(this.element.id + '_CellSelection_Container_') !== -1)) {
                    let isCellTap = false;
                    if (!heatmap.isCellTapHold) {
                        isCellTap = true;
                    }
                    let pageX;
                    let pageY;
                    let touchArg;
                    const elementRect = this.element.getBoundingClientRect();
                    if (e.originalEvent.type === 'touchend' || e.originalEvent.type === 'touchstart') {
                        this.isTouch = true;
                        touchArg = e.originalEvent;
                        pageX = touchArg.changedTouches[0].clientX;
                        pageY = touchArg.changedTouches[0].clientY;
                    }
                    else {
                        this.isTouch = false;
                        pageX = e.originalEvent.clientX;
                        pageY = e.originalEvent.clientY;
                    }
                    pageX -= elementRect.left;
                    pageY -= elementRect.top;
                    const currentRect = this.heatMapSeries.getCurrentRect(pageX, pageY);
                    window.clearTimeout(this.tooltipTimer);
                    heatmap.tooltipOnMouseMove(null, currentRect, isCellTap);
                }
            }
        });
        this.setStyle(this.element);
    }
    /**
     * Applying styles for heatmap element
     */
    setStyle(element) {
        element.style.touchAction = 'element';
        element.style.touchAction = 'element';
        element.style.zoom = 'none';
        element.style.userSelect = 'none';
        element.style.webkitUserSelect = 'none';
        element.style.position = 'relative';
        element.style.display = 'block';
    }
    /**
     * This method is used to print the rendered heatmap.
     */
    print() {
        const exportChart = new ExportUtils(this);
        exportChart.print();
    }
    /**
     * Method to unbind events for HeatMap
     */
    unWireEvents() {
        /*! Find the Events type */
        // eslint-disable-next-line
        const isIE11Pointer = Browser.isPointer;
        const start = Browser.touchStartEvent;
        const stop = Browser.touchEndEvent;
        const move = Browser.touchMoveEvent;
        const cancel = isIE11Pointer ? 'pointerleave' : 'mouseleave';
        EventHandler.remove(this.element, Browser.isDevice ? start : 'click', this.heatMapMouseClick);
        EventHandler.remove(this.element, 'contextmenu', this.heatMapMouseRightClick);
        EventHandler.remove(this.element, 'dblclick', this.heatMapMouseDoubleClick);
        EventHandler.remove(this.element, start, this.heatMapMouseMove);
        EventHandler.remove(this.element, stop, this.heatMapMouseLeave);
        EventHandler.remove(this.element, move, this.heatMapMouseMove);
        EventHandler.remove(this.element, cancel, this.heatMapMouseLeave);
        EventHandler.remove(this.element, 'keyup', this.heatMapKeyUp);
        EventHandler.remove(this.element, 'keydown', this.heatMapKeyDown);
        window.removeEventListener((Browser.isTouch && ('orientation' in window && 'onorientationchange' in window)) ? 'orientationchange' : 'resize', this.resizeEvent);
    }
    /**
     * Handles the heatmap resize.
     *
     * @returns {boolean}
     * @private
     */
    // eslint-disable-next-line
    heatMapResize(e) {
        this.resizing = true;
        const argData = {
            heatmap: this,
            cancel: false,
            name: 'resized',
            currentSize: new Size(0, 0),
            previousSize: new Size(this.availableSize.width, this.availableSize.height)
        };
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
        }
        this.resizeTimer = setTimeout(() => {
            if (this.isDestroyed) {
                clearTimeout(this.resizeTimer);
                return;
            }
            this.createSvg();
            argData.currentSize = this.availableSize;
            this.trigger('resized', argData);
            this.refreshBound();
            this.appendSvgObject();
            if (this.allowSelection) {
                this.updateCellSelection();
            }
            this.trigger('loaded', ({ heatmap: this }));
            this.resizing = false;
        }, 500);
        return false;
    }
    /**
     * Method to bind selection after window resize for HeatMap
     */
    updateCellSelection() {
        const wSize = this.initialClipRect.width / this.axisCollections[0].axisLabelSize;
        const hSize = this.initialClipRect.height / this.axisCollections[1].axisLabelSize;
        const x = this.initialClipRect.x;
        const y = this.initialClipRect.y;
        if (!this.enableCanvasRendering) {
            if (this.multiCellCollection.length !== 0) {
                const containersRect = document.getElementById(this.element.id + '_Container_RectGroup');
                const containerText = document.getElementById(this.element.id + '_Container_TextGroup');
                for (let i = 0; i < containersRect.childNodes.length; i++) {
                    containersRect.childNodes[i].setAttribute('opacity', '0.3');
                    if (this.cellSettings.showLabel && containerText.childNodes[i]) {
                        containerText.childNodes[i].setAttribute('opacity', '0.3');
                    }
                }
                for (let i = 0; i < this.multiCellCollection.length; i++) {
                    const collectionClass = this.multiCellCollection[i].cellElement;
                    const cellIndex = collectionClass.id.replace(this.element.id + '_HeatMapRect_', '');
                    const index = parseInt(cellIndex, 10);
                    containersRect.childNodes[index].setAttribute('opacity', '1');
                    if (this.cellSettings.showLabel && containerText.childNodes[i]) {
                        const getText = document.getElementById(this.element.id + '_HeatMapRectLabels_' + index);
                        if (getText) {
                            getText.setAttribute('opacity', '1');
                        }
                        this.addSvgClass(containersRect.childNodes[index]);
                    }
                }
            }
        }
        else if (this.enableCanvasRendering) {
            const rect = this.multiCellCollection;
            const oldCanvas = document.getElementById(this.element.id + '_canvas');
            const newCanvas = document.getElementById(this.element.id + '_secondary_canvas');
            const initialRect = this.initialClipRect;
            const rectHeight = initialRect.y + initialRect.height;
            const rectWidth = initialRect.x + initialRect.width;
            for (let i = 0; i < this.multiCellCollection.length; i++) {
                this.multiCellCollection[i].width = rect[i].width = wSize;
                this.multiCellCollection[i].height = rect[i].height = hSize;
                this.multiCellCollection[i].x = rect[i].x = x + wSize * this.multiCellCollection[i].xPosition;
                this.multiCellCollection[i].y = rect[i].y = y + hSize * this.multiCellCollection[i].yPosition;
                const rectImage = oldCanvas.getContext('2d').getImageData(rect[i].x, rect[i].y, rect[i].width, rect[i].height);
                newCanvas.getContext('2d').putImageData(rectImage, rect[i].x, rect[i].y);
                oldCanvas.style.opacity = '0.3';
            }
            const topPositions = oldCanvas.getContext('2d').getImageData(0, 0, this.availableSize.width, initialRect.y);
            newCanvas.getContext('2d').putImageData(topPositions, 0, 0);
            const bottomPositions = oldCanvas.getContext('2d').getImageData(0, rectHeight, this.availableSize.width, this.availableSize.height - rectHeight);
            newCanvas.getContext('2d').putImageData(bottomPositions, 0, initialRect.y + initialRect.height);
            const rightPosition = oldCanvas.getContext('2d').
                getImageData(rectWidth, 0, this.availableSize.width - rectWidth, this.availableSize.height);
            newCanvas.getContext('2d').putImageData(rightPosition, rectWidth, 0);
            const leftPosition = oldCanvas.getContext('2d').getImageData(0, 0, initialRect.x, this.availableSize.height);
            newCanvas.getContext('2d').putImageData(leftPosition, 0, 0);
            removeElement(this.element.id + '_selectedCells');
        }
    }
    clearSVGSelection() {
        const rect = document.getElementById(this.element.id + '_Container_RectGroup');
        const text = document.getElementById(this.element.id + '_Container_TextGroup');
        for (let i = 0; i < rect.childNodes.length; i++) {
            const elementClassName = rect.childNodes[i].getAttribute('class');
            if (elementClassName === this.element.id + '_selected') {
                this.removeSvgClass(rect.childNodes[i], elementClassName);
            }
            rect.childNodes[i].setAttribute('opacity', '1');
            if (this.cellSettings.showLabel && text.childNodes[i]) {
                text.childNodes[i].setAttribute('opacity', '1');
            }
        }
    }
    /**
     * Get the maximum length of data source for both horizontal and vertical
     *
     * @private
     */
    calculateMaxLength() {
        const dataSource = this.completeAdaptDataSource;
        if (dataSource && dataSource.length > 0) {
            const xAxisMax = dataSource.length - 1;
            let yAxisMax = 0;
            for (let i = 0; i <= xAxisMax; i++) {
                const length = dataSource[i].length;
                yAxisMax = yAxisMax > length ? yAxisMax : length;
            }
            this.axisCollections[0].maxLength = xAxisMax;
            this.axisCollections[1].maxLength = yAxisMax - 1;
        }
        else {
            this.axisCollections[0].maxLength = 0;
            this.axisCollections[1].maxLength = 0;
        }
    }
    /**
     * To find mouse x, y for aligned heatmap element svg position
     */
    setMouseXY(pageX, pageY) {
        const rect = this.element.getBoundingClientRect();
        let svgCanvasRect;
        if (this.enableCanvasRendering) {
            svgCanvasRect = document.getElementById(this.element.id + '_canvas').getBoundingClientRect();
        }
        else {
            svgCanvasRect = document.getElementById(this.element.id + '_svg').getBoundingClientRect();
        }
        this.mouseX = (pageX - rect.left) - Math.max(svgCanvasRect.left - rect.left, 0);
        this.mouseY = (pageY - rect.top) - Math.max(svgCanvasRect.top - rect.top, 0);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    triggerClickEvent(e, isDoubleClick, hasRightClicked) {
        let pageX;
        let pageY;
        let touchArg;
        const elementRect = this.element.getBoundingClientRect();
        if (e.type === 'touchstart') {
            if (!isDoubleClick) {
                this.isTouch = true;
            }
            touchArg = e;
            pageY = touchArg.changedTouches[0].clientY;
            pageX = touchArg.changedTouches[0].clientX;
        }
        else {
            if (!isDoubleClick) {
                this.isTouch = false;
            }
            pageY = e.clientY;
            pageX = e.clientX;
        }
        pageX -= elementRect.left;
        pageY -= elementRect.top;
        const isheatmapRect = this.isHeatmapRect(pageX, pageY);
        if (isheatmapRect) {
            const currentRect = this.heatMapSeries.getCurrentRect(pageX, pageY);
            this.trigger(isDoubleClick ? 'cellDoubleClick' : 'cellClick', {
                heatmap: this,
                value: currentRect.value,
                x: currentRect.x,
                y: currentRect.y,
                xLabel: this.heatMapSeries.hoverXAxisLabel,
                yLabel: this.heatMapSeries.hoverYAxisLabel,
                xValue: this.heatMapSeries.hoverXAxisValue,
                yValue: this.heatMapSeries.hoverYAxisValue,
                cellElement: this.enableCanvasRendering ? null : document.getElementById(currentRect.id),
                hasRightClicked: hasRightClicked,
                event: e
            });
        }
        return { x: pageX, y: pageY };
    }
    heatMapMouseRightClick(e) {
        this.triggerClickEvent(e, false, true);
    }
    heatMapMouseDoubleClick(e) {
        this.triggerClickEvent(e, true, false);
    }
    /**
     * @param {PointerEvent} e - Specifies the event.
     * @returns {boolean} Returns the boolean that that the heatmap is clicked or not
     * @private
     */
    heatMapMouseClick(e) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const position = this.triggerClickEvent(e, false, false);
        const pageX = position.x;
        const pageY = position.y;
        this.notify('click', e);
        if (this.isHeatmapRect(pageX, pageY) && this.currentRect) {
            const rectElement = document.getElementById(this.currentRect.id);
            if (!isNullOrUndefined(rectElement)) {
                rectElement.focus();
            }
        }
        if (this.paletteSettings.type !== 'Gradient' && this.legendModule
            && this.legendSettings.visible && this.legendVisibilityByCellType) {
            const page = this.legendModule.navigationCollections;
            if (page.length && pageX > page[0].x && pageX < page[0].x + page[0].width &&
                pageY > page[0].y && pageY < page[0].y + page[0].height) {
                this.legendModule.translatePage(this, this.legendModule.currentPage, true);
            }
            else if (page.length && pageX > page[1].x && pageX < page[1].x + page[1].width &&
                pageY > page[1].y && pageY < page[1].y + page[1].height) {
                this.legendModule.translatePage(this, this.legendModule.currentPage, false);
            }
            const legendRange = this.legendModule.legendRange;
            const legendTextRange = this.legendModule.legendTextRange;
            let loop = true;
            for (let i = 0; i < legendRange.length; i++) {
                if (this.legendModule && this.legendSettings.toggleVisibility &&
                    this.legendModule.currentPage === legendRange[i].currentPage) {
                    if ((loop && (pageX >= legendRange[i].x
                        && pageX <= legendRange[i].width + legendRange[i].x) &&
                        (pageY >= legendRange[i].y && pageY <= legendRange[i].y + legendRange[i].height) ||
                        ((this.legendSettings.showLabel && this.legendSettings.labelDisplayType !== 'None' &&
                            pageX >= legendTextRange[i].x
                            && pageX <= legendTextRange[i].width + legendTextRange[i].x) &&
                            (pageY >= legendTextRange[i].y
                                && pageY <= legendTextRange[i].y + legendTextRange[i].height)))) {
                        this.legendModule.legendRangeSelection(i);
                        loop = false;
                    }
                }
            }
        }
        return false;
    }
    /**
     * Handles the mouse Move.
     *
     * @returns {boolean}
     *
     * @private
     */
    heatMapMouseMove(e) {
        let pageX;
        let pageY;
        let touchArg;
        const elementRect = this.element.getBoundingClientRect();
        if (e.type === 'touchmove' || e.type === 'touchstart') {
            this.isTouch = true;
            touchArg = e;
            pageX = touchArg.changedTouches[0].clientX;
            pageY = touchArg.changedTouches[0].clientY;
        }
        else {
            this.isTouch = false;
            pageX = e.clientX;
            pageY = e.clientY;
        }
        this.removeFocus('none');
        pageX -= elementRect.left;
        pageY -= elementRect.top;
        this.setMouseXY(pageX, pageY);
        this.mouseAction(e, pageX, pageY, touchArg, elementRect);
        return true;
    }
    /**
     * Handles the mouse Move.
     *
     * @returns {boolean}
     */
    mouseAction(e, pageX, pageY, touchArg, elementRect) {
        let tooltipText;
        if (e.target && e.target.id) {
            const isheatmapRect = this.isHeatmapRect(pageX, pageY);
            if (this.legendModule) {
                if (isheatmapRect) {
                    if (this.paletteSettings.type === 'Gradient' &&
                        this.legendSettings.showGradientPointer && this.legendSettings.visible && this.legendVisibilityByCellType) {
                        this.legendModule.renderGradientPointer(e, pageX, pageY);
                    }
                }
                else {
                    this.legendModule.removeGradientPointer();
                }
                this.renderMousePointer(pageX, pageY);
            }
            let isshowTooltip;
            let currentRect;
            isshowTooltip = this.showTooltip && this.tooltipModule ? isheatmapRect : false;
            if (isheatmapRect) {
                currentRect = this.heatMapSeries.getCurrentRect(pageX, pageY);
                if (e.which !== 2 && e.which !== 3) {
                    isshowTooltip = this.cellSelectionOnMouseMove(e, currentRect, pageX, pageY, isshowTooltip);
                }
            }
            this.tooltipOnMouseMove(e, currentRect, isshowTooltip, isheatmapRect);
            if (this.legendModule && this.legendSettings.visible && this.paletteSettings.type === 'Fixed' &&
                this.legendSettings.enableSmartLegend && this.legendSettings.labelDisplayType === 'None') {
                this.legendModule.createTooltip(pageX, pageY);
            }
            if (!this.enableCanvasRendering) {
                if (this.titleSettings.text && this.titleSettings.textStyle.textOverflow === 'Trim') {
                    this.titleTooltip(e, pageX, pageY, this.isTouch);
                }
                this.axisTooltip(e, pageX, pageY, this.isTouch);
                if (this.legendModule && this.legendSettings.visible && this.legendSettings.showLabel && this.legendVisibilityByCellType) {
                    this.legendModule.renderLegendLabelTooltip(e, pageX, pageY);
                }
                if (this.legendModule && this.legendSettings.visible && this.legendVisibilityByCellType) {
                    this.legendModule.renderLegendTitleTooltip(e, pageX, pageY);
                }
            }
            else {
                // eslint-disable-next-line
                elementRect = this.element.getBoundingClientRect();
                const tooltipRect = (this.paletteSettings.type === 'Fixed' && this.legendSettings.enableSmartLegend &&
                    this.legendSettings.labelDisplayType === 'None') ? false : true;
                tooltipText = getTooltipText(this.tooltipCollection, pageX, pageY) || (this.legendModule && tooltipRect &&
                    (!isNullOrUndefined(this.legendModule.legendLabelTooltip) &&
                        getTooltipText(this.legendModule.legendLabelTooltip, pageX, pageY)
                        || !isNullOrUndefined(this.legendModule.legendTitleTooltip) &&
                            getTooltipText(this.legendModule.legendTitleTooltip, pageX, pageY)));
                if (tooltipText) {
                    showTooltip(tooltipText, pageX, pageY, this.element.offsetWidth, this.element.id + '_canvas_Tooltip', getElement(this.element.id + '_Secondary_Element'), this.isTouch, this);
                }
                else {
                    removeElement(this.element.id + '_canvas_Tooltip');
                }
            }
        }
        return true;
    }
    /**
     * Triggering cell selection
     */
    cellSelectionOnMouseMove(e, currentRect, pageX, pageY, isshowTooltip) {
        if ((this.cellSettings.tileType === 'Rect' && e.type === 'mousedown' || e.type === 'touchstart'
            || e.type === 'pointerdown') && this.allowSelection) {
            this.previousRect = currentRect;
            this.multiSelection = true;
            this.rectSelected = true;
            this.initialCellX = pageX;
            this.initialCellY = pageY;
            e.preventDefault();
        }
        if (this.cellSettings.tileType === 'Rect' && this.multiSelection && currentRect) {
            isshowTooltip = false;
            this.highlightSelectedCells(this.previousRect, currentRect, pageX, pageY, e);
        }
        return isshowTooltip;
    }
    /**
     * Rendering tooltip on mouse move
     */
    tooltipOnMouseMove(e, currentRect, isshowTooltip, isheatmapRect) {
        if (isshowTooltip && currentRect) {
            if (this.tempTooltipRectId !== currentRect.id) {
                if (this.showTooltip) {
                    if ((this.cellSettings.enableCellHighlighting || (this.tooltipModule && this.showTooltip))
                        && !this.enableCanvasRendering) {
                        this.heatMapSeries.highlightSvgRect(currentRect.id);
                    }
                    if (this.tooltipTimer && !isNullOrUndefined(e) && (e.type === 'touchstart' || e.type === 'touchmove')) {
                        window.clearTimeout(this.tooltipTimer);
                        this.tooltipTimer = null;
                    }
                    this.tooltipModule.renderTooltip(currentRect);
                    const tooltipObject = this.tooltipModule.tooltipObject;
                    if (this.isTouch) {
                        this.tooltipTimer = setTimeout(() => {
                            if (!isNullOrUndefined(tooltipObject) &&
                                !isNullOrUndefined(document.getElementById(tooltipObject.element.id))
                                && !isNullOrUndefined(document.getElementById(tooltipObject.element.id).firstChild)) {
                                tooltipObject.fadeOut();
                            }
                            this.tooltipModule.isFadeout = true;
                            window.clearTimeout(this.tooltipTimer);
                            this.tooltipTimer = null;
                        }, 1500);
                        if (e) {
                            if (e.type === 'touchmove') {
                                if (e.cancelable) {
                                    e.preventDefault();
                                }
                            }
                        }
                    }
                }
                this.tempTooltipRectId = currentRect.id;
            }
        }
        else {
            if (e !== null) {
                const borderBoundary = 5;
                if (!isheatmapRect) {
                    if ((this.cellSettings.enableCellHighlighting || this.showTooltip) && !this.enableCanvasRendering &&
                        this.cellSettings.border.width < borderBoundary) {
                        this.heatMapSeries.highlightSvgRect(e.target.id);
                    }
                    if (this.tooltipModule && this.showTooltip) {
                        this.tooltipModule.showHideTooltip(false, true);
                    }
                }
                else if (!this.showTooltip && this.cellSettings.border.width > borderBoundary) {
                    this.heatMapSeries.highlightSvgRect(e.target.id);
                }
            }
            this.tempTooltipRectId = '';
        }
    }
    /**
     * To select the multiple cells on mouse move action
     */
    highlightSelectedCells(previousRect, currentRect, pageX, pageY, e) {
        const pXIndex = previousRect.xIndex;
        const pYIndex = previousRect.yIndex;
        const cXIndex = currentRect.xIndex;
        const cYIndex = currentRect.yIndex;
        this.currentRect = currentRect;
        this.selectedCellsRect = new Rect(0, 0, 0, 0);
        this.selectedCellsRect.x = previousRect.x > currentRect.x ? currentRect.x : previousRect.x;
        this.selectedCellsRect.y = previousRect.y > currentRect.y ? currentRect.y : previousRect.y;
        this.selectedCellsRect.width = ((previousRect.x > currentRect.x ? (pXIndex - cXIndex) :
            (cXIndex - pXIndex)) + 1) * currentRect.width;
        this.selectedCellsRect.height = ((previousRect.y > currentRect.y ? (pYIndex - cYIndex) :
            (cYIndex - pYIndex)) + 1) * currentRect.height;
        if (e.type === 'touchstart' && this.multiCellCollection.length === 0 && !this.isCellTapHold) {
            this.isCellTapHold = true;
        }
        else {
            this.isCellTapHold = false;
        }
        e.preventDefault();
        const x = this.initialCellX > pageX ? pageX : this.initialCellX;
        const y = this.initialCellY > pageY ? pageY : this.initialCellY;
        const rect = new Rect(x - this.initialClipRect.x, y - this.initialClipRect.y, Math.abs(pageX - this.initialCellX), Math.abs(pageY - this.initialCellY));
        if (((rect.width > 0) && this.enableMultiSelect && e.ctrlKey === false)) {
            this.removeSelectedCellsBorder(false);
            const tooltipElement = document.getElementById(this.element.id + 'Celltooltipcontainer_svg');
            if (tooltipElement) {
                this.tooltipModule.tooltipObject = null;
                tooltipElement.setAttribute('opacity', '0');
            }
        }
        const parentDiv = document.getElementById(this.element.id + '_CellSelection_Container');
        const svgObject = this.renderer.createSvg({
            id: this.element.id + '_CellSelection_Container_svg',
            width: this.initialClipRect.width,
            height: this.initialClipRect.height
        });
        parentDiv.appendChild(svgObject);
        const parent = document.getElementById(this.element.id + '_CellSelection_Container_svg');
        if (this.enableMultiSelect) {
            const rectItems = new RectOption(this.element.id + '_selectedCells', '#87ceeb', { color: 'transparent', width: 1 }, 1, rect, '#0000ff');
            parent.appendChild(this.renderer.drawRectangle(rectItems));
            document.getElementById(this.element.id + '_selectedCells').style.opacity = '0.5';
        }
    }
    /**
     * Method to get selected cell data collection for HeatMap
     */
    getDataCollection() {
        if (!isNullOrUndefined(this.previousRect) && !isNullOrUndefined(this.currentRect)) {
            const pXIndex = this.previousRect.xIndex;
            const pYIndex = this.previousRect.yIndex;
            const cXIndex = this.currentRect.xIndex;
            const cYIndex = this.currentRect.yIndex;
            const minX = cXIndex > pXIndex ? pXIndex : cXIndex;
            const maxX = cXIndex > pXIndex ? cXIndex : pXIndex;
            const minY = cYIndex > pYIndex ? pYIndex : cYIndex;
            const maxY = cYIndex > pYIndex ? cYIndex : pYIndex;
            let tempX = minX;
            let tempY = minY;
            let cellX = this.previousRect.x;
            let cellY = this.previousRect.y;
            this.getCellCollection(this.currentRect, this.previousRect, true, tempX, tempY, maxX, maxY, minX, cellX, cellY);
            tempX = minX;
            tempY = minY;
            cellX = this.previousRect.x;
            cellY = this.previousRect.y;
            this.checkSelectedCells();
            this.getCellCollection(this.currentRect, this.previousRect, false, tempX, tempY, maxX, maxY, minX, cellX, cellY);
            this.selectedMultiCellCollection = [];
            this.canvasSelectedCells = new Rect(0, 0, 0, 0);
            this.selectedCellCount = 0;
        }
    }
    /**
     * To get the selected datas.
     */
    getCellCollection(currentRect, previousRect, singleCellData, tempX, tempY, maxX, maxY, minX, cellX, cellY) {
        const xIndex = Math.abs((currentRect.xIndex === previousRect.xIndex ?
            0 : currentRect.xIndex - previousRect.xIndex)) + 1;
        const yIndex = Math.abs((currentRect.yIndex === previousRect.yIndex ?
            0 : currentRect.yIndex - previousRect.yIndex)) + 1;
        for (let i = 0; i < (xIndex * yIndex); i++) {
            if (singleCellData) {
                this.getSelectedCellData(cellX, cellY, true);
            }
            else {
                this.getSelectedCellData(cellX, cellY, false);
            }
            if (tempX < maxX) {
                cellX += currentRect.xIndex > previousRect.xIndex ? currentRect.width : -currentRect.width;
                tempX++;
            }
            else if (tempY < maxY) {
                cellY += currentRect.yIndex > previousRect.yIndex ? currentRect.height : -currentRect.height;
                cellX = previousRect.x;
                tempX = minX;
            }
        }
    }
    /**
     * To remove the selection on mouse click without ctrl key.
     */
    removeSelectedCellsBorder(isSelectionCancel) {
        if (!this.enableCanvasRendering) {
            const containerRect = document.getElementById(this.element.id + '_Container_RectGroup');
            const containerText = document.getElementById(this.element.id + '_Container_TextGroup');
            for (let i = 0; i < containerRect.childNodes.length; i++) {
                const elementClassName = containerRect.childNodes[i].getAttribute('class');
                if (isSelectionCancel) {
                    containerRect.childNodes[i].setAttribute('opacity', '1');
                    if (this.cellSettings.showLabel && containerText.childNodes[i]) {
                        containerText.childNodes[i].setAttribute('opacity', '1');
                        this.removeSvgClass(containerRect.childNodes[i], elementClassName);
                    }
                }
                else {
                    containerRect.childNodes[i].setAttribute('opacity', '0.3');
                    if (this.cellSettings.showLabel && containerText.childNodes[i]) {
                        containerText.childNodes[i].setAttribute('opacity', '0.3');
                        this.removeSvgClass(containerRect.childNodes[i], elementClassName);
                    }
                }
            }
        }
        else {
            const ctx = this.secondaryCanvasRenderer.ctx;
            for (let i = 0; i < this.previousSelectedCellsRect.length; i++) {
                const rect = this.previousSelectedCellsRect[i];
                ctx.save();
                ctx.clearRect(rect.x - 1, rect.y - 1, rect.width + 2, rect.height + 2);
                ctx.restore();
            }
            for (let i = 0; i < this.multiCellCollection.length; i++) {
                const rects = this.multiCellCollection[i];
                if (this.multiCellCollection.length > 0) {
                    ctx.save();
                    ctx.clearRect(rects.x - 1, rects.y - 1, rects.width + 2, rects.height + 2);
                }
            }
        }
    }
    /**
     * To highlight the selected multiple cells on mouse move action in canvas mode.
     */
    highlightSelectedAreaInCanvas(rect) {
        if (rect.x) {
            const oldCanvas = document.getElementById(this.element.id + '_canvas');
            const newCanvas = document.getElementById(this.element.id + '_secondary_canvas');
            const initialRect = this.initialClipRect;
            const rectImage = oldCanvas.getContext('2d').getImageData(rect.x, rect.y, rect.width, rect.height);
            newCanvas.getContext('2d').putImageData(rectImage, rect.x, rect.y);
            oldCanvas.style.opacity = '0.3';
            const topPosition = oldCanvas.getContext('2d').getImageData(0, 0, this.availableSize.width, initialRect.y);
            newCanvas.getContext('2d').putImageData(topPosition, 0, 0);
            const bottomPosition = oldCanvas.getContext('2d').getImageData(0, initialRect.y + initialRect.height, this.availableSize.width, this.availableSize.height - (initialRect.y + initialRect.height));
            newCanvas.getContext('2d').putImageData(bottomPosition, 0, initialRect.y + initialRect.height);
            const rightPosition = oldCanvas.getContext('2d').getImageData(initialRect.x + initialRect.width, 0, this.availableSize.width - (initialRect.x + initialRect.width), this.availableSize.height);
            newCanvas.getContext('2d').putImageData(rightPosition, initialRect.x + initialRect.width, 0);
            const leftPosition = oldCanvas.getContext('2d').getImageData(0, 0, initialRect.x, this.availableSize.height);
            newCanvas.getContext('2d').putImageData(leftPosition, 0, 0);
        }
    }
    /**
     * To get the collection of selected cells.
     */
    getSelectedCellData(cellX, cellY, cellCollection) {
        const xAxis = this.axisCollections[0];
        const yAxis = this.axisCollections[1];
        const xLabels = xAxis.tooltipLabels;
        const yLabels = yAxis.tooltipLabels.slice().reverse();
        const rectPosition = this.heatMapSeries.getCurrentRect(cellX + 1, cellY + 1);
        const currentRect = document.getElementById(rectPosition.id);
        const cellDetails = new SelectedCellDetails(null, '', '', 0, 0, null, 0, 0, 0, 0, 0, 0);
        cellDetails.value = rectPosition.value;
        cellDetails.xLabel = xLabels[rectPosition.xIndex].toString();
        cellDetails.yLabel = yLabels[rectPosition.yIndex].toString();
        cellDetails.xValue = xAxis.labelValue[rectPosition.xIndex];
        cellDetails.yValue = yAxis.labelValue.slice().reverse()[rectPosition.yIndex];
        cellDetails.cellElement = this.enableCanvasRendering ? null : currentRect;
        cellDetails.xPosition = rectPosition.xIndex;
        cellDetails.yPosition = rectPosition.yIndex;
        cellDetails.width = this.currentRect.width;
        cellDetails.height = this.currentRect.height;
        cellDetails.x = this.currentRect.x;
        cellDetails.y = this.currentRect.y;
        this.currentRect.allowCollection = true;
        this.addSvgClass(currentRect);
        if (cellCollection) {
            this.selectedMultiCellCollection.push(cellDetails);
            this.currentRect.allowCollection = false;
        }
        else {
            for (let i = 0; i < this.multiCellCollection.length; i++) {
                if (this.multiCellCollection[i].xPosition === cellDetails.xPosition &&
                    this.multiCellCollection[i].yPosition === cellDetails.yPosition) {
                    this.currentRect.allowCollection = false;
                    if (this.selectedCellCount === this.selectedMultiCellCollection.length) {
                        this.currentRect.allowCollection = false;
                        if (!this.enableCanvasRendering) {
                            for (let j = 0; j < this.selectedMultiCellCollection.length; j++) {
                                const rectElement = this.selectedMultiCellCollection[j].cellElement;
                                if (rectElement) {
                                    const index = rectElement.id.replace(this.element.id + '_HeatMapRect_', '');
                                    // eslint-disable-next-line
                                    const containerText = document.getElementById(this.element.id + '_Container_TextGroup');
                                    const elementClassName = rectElement.getAttribute('class');
                                    rectElement.setAttribute('opacity', '0.3');
                                    const getText = document.getElementById(this.element.id + '_HeatMapRectLabels_' + index);
                                    if (getText) {
                                        getText.setAttribute('opacity', '0.3');
                                    }
                                    this.removeSvgClass(rectElement, elementClassName);
                                }
                            }
                        }
                        else {
                            const ctx = this.secondaryCanvasRenderer.ctx;
                            const rect = this.canvasSelectedCells;
                            ctx.save();
                            ctx.clearRect(rect.x - 1, rect.y - 1, rect.width + 2, rect.height + 2);
                            ctx.restore();
                            this.selectedCellsRect = new Rect(0, 0, 0, 0);
                        }
                        this.multiCellCollection.splice(i, 1);
                    }
                }
            }
        }
        if (rectPosition.visible && !isNullOrUndefined(rectPosition.value) && this.currentRect.allowCollection === true) {
            this.multiCellCollection.push(cellDetails);
        }
    }
    /**
     * To add class for selected cells
     *
     * @private
     */
    addSvgClass(element) {
        if (!this.enableCanvasRendering) {
            const className = this.element.id + '_selected';
            element.classList.add(className);
        }
    }
    /**
     * To remove class for unselected cells
     *
     * @private
     */
    removeSvgClass(rectElement, className) {
        if (className) {
            rectElement.setAttribute('class', className.replace(className, ''));
        }
    }
    /**
     * This method is used to clear the cell selection in the heatmap.
     * {% codeBlock src='heatmap/clearSelection/index.md' %}{% endcodeBlock %}
     */
    clearSelection() {
        if (!this.enableCanvasRendering && this.allowSelection) {
            this.clearSVGSelection();
        }
        if (this.enableCanvasRendering) {
            const ctx = this.secondaryCanvasRenderer.ctx;
            for (let i = 0; i < this.previousSelectedCellsRect.length; i++) {
                ctx.save();
                ctx.clearRect(this.previousSelectedCellsRect[i].x - 1, this.previousSelectedCellsRect[i].y - 1, this.previousSelectedCellsRect[i].width + 2, this.previousSelectedCellsRect[i].height + 2);
                ctx.restore();
            }
            for (let i = 0; i < this.multiCellCollection.length; i++) {
                const rects = this.multiCellCollection[i];
                if (this.multiCellCollection.length > 0) {
                    ctx.save();
                    ctx.clearRect(rects.x - 1, rects.y - 1, rects.width + 2, rects.height + 2);
                }
            }
            const canvas = document.getElementById(this.element.id + '_canvas');
            canvas.style.opacity = '1';
        }
        this.tempMultiCellCollection = [];
        this.multiCellCollection = [];
        this.rectSelected = false;
    }
    renderMousePointer(pageX, pageY) {
        const legendRange = this.legendModule.legendRange;
        const legendTextRange = this.legendModule.legendTextRange;
        let loop = true;
        for (let i = 0; i < legendRange.length; i++) {
            if (this.legendSettings.toggleVisibility && this.legendModule.currentPage === legendRange[i].currentPage) {
                if ((loop && (pageX >= legendRange[i].x
                    && pageX <= legendRange[i].width + legendRange[i].x) &&
                    (pageY >= legendRange[i].y && pageY <= legendRange[i].y + legendRange[i].height) ||
                    ((this.legendSettings.showLabel && this.legendSettings.labelDisplayType !== 'None' &&
                        pageX >= legendTextRange[i].x
                        && pageX <= legendTextRange[i].width + legendTextRange[i].x) &&
                        (pageY >= legendTextRange[i].y
                            && pageY <= legendTextRange[i].y + legendTextRange[i].height)))) {
                    if (this.enableCanvasRendering) {
                        document.getElementById(this.element.id + '_canvas').style.cursor = 'Pointer';
                    }
                    else {
                        document.getElementById(this.element.id + '_svg').style.cursor = 'Pointer';
                        const legendLabelTooltipContainer = document.getElementById(this.element.id + 'legendLabelTooltipContainer');
                        if (!isNullOrUndefined(legendLabelTooltipContainer)) {
                            legendLabelTooltipContainer.style.cursor = 'Pointer';
                        }
                    }
                    loop = false;
                }
                else if (loop) {
                    if (this.enableCanvasRendering) {
                        document.getElementById(this.element.id + '_canvas').style.cursor = '';
                    }
                    else {
                        document.getElementById(this.element.id + '_svg').style.cursor = '';
                    }
                }
            }
        }
    }
    /**
     * Handles the mouse end.
     *
     * @returns {boolean}
     * @private
     */
    heatMapMouseLeave(e) {
        if (e.target && e.target.id &&
            (this.cellSettings.enableCellHighlighting || (this.tooltipModule && this.showTooltip))
            && !this.enableCanvasRendering) {
            this.heatMapSeries.highlightSvgRect(this.tempTooltipRectId);
        }
        if (this.allowSelection && this.multiSelection) {
            this.multiSelection = false;
            if (e.type === 'mouseup' || e.type === 'mouseleave' || e.type === 'touchend' || e.type === 'pointerup') {
                if (e.which !== 2 && e.which !== 3) {
                    if (this.isCellTapHold === false) {
                        let rect;
                        const selectionRect = document.getElementById(this.element.id + '_selectedCells');
                        if (!isNullOrUndefined(selectionRect)) {
                            const rectBound = selectionRect.getClientRects()[0];
                            rect = new Rect(rectBound.left, rectBound.top, rectBound.width, rectBound.height);
                        }
                        else {
                            let pageX;
                            let pageY;
                            let touchArg;
                            const elementRect = this.element.getBoundingClientRect();
                            if (e.type === 'touchend') {
                                this.isTouch = true;
                                touchArg = e;
                                pageY = touchArg.changedTouches[0].clientY;
                                pageX = touchArg.changedTouches[0].clientX;
                            }
                            else {
                                this.isTouch = false;
                                pageY = e.clientY;
                                pageX = e.clientX;
                            }
                            pageX -= elementRect.left;
                            pageY -= elementRect.top;
                            const x = this.initialCellX > pageX ? pageX : this.initialCellX;
                            const y = this.initialCellY > pageY ? pageY : this.initialCellY;
                            rect = new Rect(x - this.initialClipRect.x, y - this.initialClipRect.y, Math.abs(pageX - this.initialCellX) !== 0 ? Math.abs(pageX - this.initialCellX) :
                                Math.abs(pageY - this.initialCellY), Math.abs(pageY - this.initialCellY));
                        }
                        if (!(rect.width > 0 && !this.enableMultiSelect)) {
                            const selectedCellCollection = [];
                            for (let i = 0; i < this.multiCellCollection.length; i++) {
                                selectedCellCollection.push(this.multiCellCollection[i]);
                            }
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            if (e.ctrlKey === false || !this.enableMultiSelect) {
                                this.multiCellCollection = [];
                            }
                            this.getDataCollection();
                            const argData = {
                                heatmap: this,
                                cancel: false,
                                name: 'cellSelected',
                                data: this.multiCellCollection
                            };
                            this.trigger('cellSelected', argData);
                            if (!argData.cancel) {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                if (e.ctrlKey === false || !this.enableMultiSelect) {
                                    this.removeSelectedCellsBorder(false);
                                }
                                this.currentRect.allowCollection = false;
                            }
                            else {
                                this.multiCellCollection = selectedCellCollection;
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                if (this.multiCellCollection.length > 0 || e.ctrlKey === false || !this.enableMultiSelect) {
                                    this.removeSelectedCellsBorder(true);
                                }
                            }
                            this.setCellOpacity();
                            if (e.type === 'touchend') {
                                window.clearTimeout(this.tooltipTimer);
                                this.tooltipOnMouseMove(null, this.currentRect, true);
                            }
                        }
                    }
                    else {
                        this.isCellTapHold = false;
                    }
                }
            }
        }
        if (this.tooltipModule && this.showTooltip && e.type === 'mouseleave') {
            this.tooltipModule.showHideTooltip(false);
        }
        this.tempTooltipRectId = '';
        if (this.legendModule && this.legendSettings.visible && this.legendModule.tooltipObject &&
            this.legendModule.tooltipObject.element) {
            const tooltipElement = this.legendModule.tooltipObject.element.firstChild;
            if (e.type === 'mouseleave') {
                tooltipElement.setAttribute('opacity', '0');
            }
            else {
                if (this.legendTooltipTimer) {
                    window.clearTimeout(this.legendTooltipTimer);
                }
                this.legendTooltipTimer = setTimeout(() => {
                    tooltipElement.setAttribute('opacity', '0');
                }, 1500);
            }
        }
        if (this.paletteSettings.type === 'Gradient' && this.legendModule && this.legendSettings.showGradientPointer &&
            this.legendSettings.visible && this.legendVisibilityByCellType) {
            if (e.type === 'mouseleave') {
                this.legendModule.removeGradientPointer();
            }
            else {
                if (this.gradientTimer) {
                    window.clearTimeout(this.gradientTimer);
                }
                this.gradientTimer = setTimeout(() => { this.legendModule.removeGradientPointer(); }, 1500);
            }
        }
        if (this.enableCanvasRendering) {
            const main = document.getElementById(this.element.id + '_hoverRect_canvas');
            if (main) {
                main.style.visibility = 'hidden';
                this.tempRectHoverClass = '';
            }
        }
        if (this.titleSettings.text && this.titleCollection[0].indexOf('...') !== -1) {
            if (e.cancelable) {
                e.preventDefault();
            }
            if (!this.isTouch) {
                if (!this.enableCanvasRendering) {
                    removeElement(this.element.id + '_Title_Tooltip');
                }
                else {
                    removeElement(this.element.id + '_canvas_Tooltip');
                }
            }
        }
        return true;
    }
    /**
     * This method is used to perform operations when keyboard up on Heatmap.
     *
     * @param {KeyboardEvent} e - Specifies the keyboard event on Heatmap.
     * @returns {void}
     * @private
     */
    heatMapKeyUp(e) {
        if (e.code !== 'Tab') {
            return;
        }
        this.removeFocus('none');
        if (this.tooltipModule) {
            this.tooltipModule.showHideTooltip(false);
        }
        const targetElement = e.target;
        const isRect = targetElement.id.indexOf('HeatMapRect') > -1;
        const isLegend = targetElement.id.indexOf('Legend') > -1;
        const toHighlightCells = this.cellSettings.enableCellHighlighting;
        if (toHighlightCells && !isLegend && !this.rectSelected) {
            this.heatMapSeries.highlightSvgRect(targetElement.id);
        }
        else if ((isRect && this.allowSelection) || (isLegend && this.legendSettings.toggleVisibility) || targetElement.id.indexOf('arrow') > -1) {
            targetElement.style.outline = '2px solid black';
            targetElement.classList.add('keyboard-focused');
        }
    }
    /**
     * This method is used to perform operations when keyboard down on Heatmap.
     *
     * @param {KeyboardEvent} e - Specifies the keyboard event on Heatmap.
     * @returns {void}
     * @private
     */
    heatMapKeyDown(e) {
        if (e.code !== 'Enter') {
            return;
        }
        if (!e.ctrlKey) {
            this.multiCellCollection = [];
        }
        this.removeFocus('none');
        let targetElement = e.target;
        if (this.allowSelection && (targetElement.id.indexOf('HeatMapRect') > -1) && this.cellSettings.tileType === 'Rect') {
            this.previousRect = this.currentRect = this.getRectElement(targetElement.id);
            this.removeSelectedCellsBorder(false);
            this.getDataCollection();
            this.setCellOpacity();
            this.rectSelected = true;
        }
        else if (this.legendModule && this.legendSettings.visible) {
            const index = this.calculateLegendIndex(targetElement);
            if (!isNullOrUndefined(index)) {
                this.legendModule.legendRangeSelection(index);
            }
            targetElement = document.getElementById(targetElement.id);
            this.handleArrowNavigation(targetElement);
            targetElement.setAttribute('tabindex', '0');
            targetElement.focus();
        }
    }
    /**
     * Method to find the legend index.
     */
    calculateLegendIndex(targetElement) {
        if (this.legendSettings.toggleVisibility) {
            if (targetElement.id.indexOf('Legend_Index') > -1) {
                return parseFloat(targetElement.id.split('Legend_Index_')[1]);
            }
            if (targetElement.id.indexOf('_Smart_Legend_Group_') > -1) {
                return parseFloat(targetElement.id.split('_Smart_Legend_Group_')[1]);
            }
        }
        return null;
    }
    /**
     * Method to handle arrow navigation in legend.
     */
    handleArrowNavigation(targetElement) {
        if (targetElement.id.indexOf('arrow') > -1) {
            const currentPage = this.legendModule.currentPage;
            const maxPage = this.legendColorCollection.length / this.legendModule.listPerPage;
            if (currentPage < maxPage && targetElement.id.indexOf('rightarrow') > -1) {
                this.legendModule.translatePage(this, currentPage, true);
            }
            else if (currentPage <= maxPage && targetElement.id.indexOf('leftarrow') > -1) {
                this.legendModule.translatePage(this, currentPage, false);
            }
        }
    }
    /**
     * Method to return Current rect.
     */
    getRectElement(id) {
        const rectCollection = this.heatMapSeries.rectPositionCollection;
        for (let i = 0; i < rectCollection.length; i++) {
            for (let j = 0; j < rectCollection[i].length; j++) {
                if (rectCollection[i][j].id === id) {
                    return rectCollection[i][j];
                }
            }
        }
        return null;
    }
    /**
     * Method to remove the highlight outline.
     */
    removeFocus(outline) {
        const highlightedElement = document.querySelector('.keyboard-focused');
        if (highlightedElement) {
            highlightedElement.style.outline = outline;
            highlightedElement.classList.remove('keyboard-focused');
        }
    }
    /**
     * Method to Check for deselection of cell.
     */
    checkSelectedCells() {
        if (!this.enableCanvasRendering) {
            for (let i = 0; i < this.multiCellCollection.length; i++) {
                for (let j = 0; j < this.selectedMultiCellCollection.length; j++) {
                    if (this.selectedMultiCellCollection[j].cellElement.getAttribute('id')
                        === this.multiCellCollection[i].cellElement.getAttribute('id')) {
                        this.selectedCellCount++;
                    }
                }
            }
        }
        else {
            this.canvasSelectedCells = new Rect(0, 0, 0, 0);
            this.canvasSelectedCells.x = this.selectedCellsRect.x;
            this.canvasSelectedCells.y = this.selectedCellsRect.y;
            this.canvasSelectedCells.width = this.selectedCellsRect.width;
            this.canvasSelectedCells.height = this.selectedCellsRect.height;
            for (let i = 0; i < this.multiCellCollection.length; i++) {
                for (let j = 0; j < this.selectedMultiCellCollection.length; j++) {
                    if (this.selectedMultiCellCollection[j].xPosition === this.multiCellCollection[i].xPosition &&
                        this.selectedMultiCellCollection[j].yPosition === this.multiCellCollection[i].yPosition) {
                        this.selectedCellCount++;
                    }
                }
            }
            if (this.legendModule && this.rectSelected && this.paletteSettings.type === 'Gradient') {
                this.legendModule.removeGradientPointer();
            }
        }
    }
    /**
     * Method to remove opacity for text of selected cell for HeatMap
     */
    removeOpacity(containersRect, containerText) {
        for (let i = 0; i < containersRect.childNodes.length; i++) {
            containersRect.childNodes[i].setAttribute('opacity', '0.3');
            if (this.cellSettings.showLabel && containerText.childNodes[i]) {
                containerText.childNodes[i].setAttribute('opacity', '0.3');
            }
        }
    }
    /**
     * Method to set opacity for selected cell for HeatMap
     */
    setCellOpacity() {
        if (!this.enableCanvasRendering) {
            if (this.multiCellCollection.length !== 0) {
                this.tempMultiCellCollection.push(this.multiCellCollection);
                const containersRect = document.getElementById(this.element.id + '_Container_RectGroup');
                const containerText = document.getElementById(this.element.id + '_Container_TextGroup');
                this.removeOpacity(containersRect, containerText);
                for (let i = 0; i < this.multiCellCollection.length; i++) {
                    const collectionClasss = this.multiCellCollection[i].cellElement;
                    const index = parseInt(collectionClasss.id.replace(this.element.id + '_HeatMapRect_', ''), 10);
                    containersRect.childNodes[index].setAttribute('opacity', '1');
                    containersRect.childNodes[index].setAttribute('tabindex', '0');
                    if (this.cellSettings.showLabel) {
                        const getText = document.getElementById(this.element.id + '_HeatMapRectLabels_' + index);
                        if (getText) {
                            getText.setAttribute('opacity', '1');
                        }
                    }
                }
            }
        }
        else {
            this.previousSelectedCellsRect.push(this.selectedCellsRect);
            this.highlightSelectedAreaInCanvas(this.selectedCellsRect);
        }
        removeElement(this.element.id + '_selectedCells');
    }
    /**
     * To create div container for rendering two layers of canvas.
     *
     * @returns {void}
     * @private
     */
    createMultiCellDiv(onLoad) {
        if (onLoad) {
            const divElement = this.createElement('div', {
                id: this.element.id + '_Multi_CellSelection_Canvas'
            });
            divElement.style.position = 'relative';
            this.element.appendChild(divElement);
            this.svgObject.style.position = 'absolute';
            this.svgObject.style.left = '0px';
            this.svgObject.style.top = '0px';
            this.svgObject.style.zIndex = '0';
        }
        else {
            let secondaryCanvas;
            secondaryCanvas = document.getElementById(this.element.id + '_secondary_canvas');
            if (isNullOrUndefined(secondaryCanvas)) {
                secondaryCanvas = this.secondaryCanvasRenderer.createCanvas({
                    width: this.availableSize.width,
                    height: this.availableSize.height, x: 0, y: 0
                });
            }
            secondaryCanvas.style.cssText = 'position: relative; z-index: 1';
            this.element.appendChild(secondaryCanvas);
        }
    }
};
__decorate([
    Property(null)
], HeatMap.prototype, "width", void 0);
__decorate([
    Property(null)
], HeatMap.prototype, "height", void 0);
__decorate([
    Property(true)
], HeatMap.prototype, "showTooltip", void 0);
__decorate([
    Event()
], HeatMap.prototype, "tooltipRender", void 0);
__decorate([
    Event()
], HeatMap.prototype, "resized", void 0);
__decorate([
    Event()
], HeatMap.prototype, "loaded", void 0);
__decorate([
    Event()
], HeatMap.prototype, "cellRender", void 0);
__decorate([
    Event()
], HeatMap.prototype, "cellSelected", void 0);
__decorate([
    Property('SVG')
], HeatMap.prototype, "renderingMode", void 0);
__decorate([
    Property(null)
], HeatMap.prototype, "dataSource", void 0);
__decorate([
    Complex({}, Data)
], HeatMap.prototype, "dataSourceSettings", void 0);
__decorate([
    Property(null)
], HeatMap.prototype, "backgroundColor", void 0);
__decorate([
    Property('Material')
], HeatMap.prototype, "theme", void 0);
__decorate([
    Property(false)
], HeatMap.prototype, "allowSelection", void 0);
__decorate([
    Property(true)
], HeatMap.prototype, "enableMultiSelect", void 0);
__decorate([
    Property(false)
], HeatMap.prototype, "enableHtmlSanitizer", void 0);
__decorate([
    Complex({}, Margin)
], HeatMap.prototype, "margin", void 0);
__decorate([
    Complex({ text: '', textStyle: Theme.heatMapTitleFont }, Title)
], HeatMap.prototype, "titleSettings", void 0);
__decorate([
    Complex({}, Axis)
], HeatMap.prototype, "xAxis", void 0);
__decorate([
    Complex({ position: 'Right' }, LegendSettings)
], HeatMap.prototype, "legendSettings", void 0);
__decorate([
    Complex({}, PaletteSettings)
], HeatMap.prototype, "paletteSettings", void 0);
__decorate([
    Complex({}, TooltipSettings)
], HeatMap.prototype, "tooltipSettings", void 0);
__decorate([
    Complex({}, Axis)
], HeatMap.prototype, "yAxis", void 0);
__decorate([
    Complex({}, CellSettings)
], HeatMap.prototype, "cellSettings", void 0);
__decorate([
    Event()
], HeatMap.prototype, "created", void 0);
__decorate([
    Event()
], HeatMap.prototype, "load", void 0);
__decorate([
    Event()
], HeatMap.prototype, "cellClick", void 0);
__decorate([
    Event()
], HeatMap.prototype, "cellDoubleClick", void 0);
__decorate([
    Event()
], HeatMap.prototype, "legendRender", void 0);
HeatMap = __decorate([
    NotifyPropertyChanges
], HeatMap);
export { HeatMap };
